import React, { useState } from 'react';
import styled from 'react-emotion';
import MaxWidth from '../../Layout/MaxWidth';
import { SmallButton, SmallButtonStyle } from '../../ui/Button';
import PageHeader from '../../Layout/PageHeader/PageHeader';
import PageHeaderLoadingState from '../../Layout/PageHeader/PageHeaderLoadingState';
import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuItems,
  DropdownMenuItem
} from '@jetshop/ui/DropdownMenu';
import { ReactComponent as Carrot } from '../../../svg/Carrot.svg';
import useShopId from '../../../utils/useShopId';
import { eztradStyling } from './EztradStyling';
import { Above } from '@jetshop/ui/Breakpoints';
import { Link } from 'react-router-dom';

const ButtonContainer = styled(MaxWidth)`
  .letter-buttons-container {
    text-align: center;
    margin: 2.625rem -0.25rem 0;
  }

  ${({ theme }) => theme.below.sm} {
    padding: 0;

    .letter-buttons-container {
      text-align: center;
      margin-top: 1.875rem;
      overflow-x: auto;
      margin-left: 0;
      margin-right: 0;
      padding: 0 0.5rem;
      white-space: nowrap;
    }
  }
`;

const DropdownMenuContainer = styled(MaxWidth)`
  margin-top: 1.375rem;
  [data-flight-dropdown] {
    [data-flight-dropdown-button] {
      font-weight: 500;
      font-variant: small-caps;
      text-transform: lowercase;
      background-color: transparent;
      align-items: center;
      justify-content: space-between;
      border-radius: 0;
    }

    [data-flight-dropdown-button],
    [data-flight-dropdown-item] {
      width: 100%;
      height: 50px;
      border: 1px solid ${({ theme }) => theme.colors.lightGreen};
      font-size: 0.9375rem;
      letter-spacing: 0.05em;
      padding: 0 1.375rem;
      display: flex;
      color: ${({ theme }) => theme.colors.almostBlack};
      svg path {
        stroke: ${({ theme }) => theme.colors.black};
      }
      :focus {
        outline: none;
      }
    }

    [data-flight-dropdown-items] {
      width: 100%;
    }

    [data-flight-dropdown-item] {
      border-top: none;
    }
  }
`;

const DropdownMenuItemButton = styled('button')`
  font-weight: ${({ isActive }) => (isActive ? '700' : 'inherit')};
  background-color: transparent;
`;

const StyledButton = styled(SmallButton)`
  margin: 0 0.25rem 0.25rem;
`;

const StyledLink = styled(Link, {
  shouldForwardProp: prop => ['isActive'].indexOf(prop) === -1
})`
  ${SmallButtonStyle};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  margin: 0 0.25rem 0.25rem;
`;

const StyledMobileLink = styled(Link, {
  shouldForwardProp: prop => ['isActive'].indexOf(prop) === -1
})`
  text-decoration: none;
  color: inherit;
  align-items: center;
  display: flex;
  font-weight: ${({ isActive }) => (isActive ? 700 : 'inherit')};
`;

const Buttons = ({ buttonList, activeKey, setActiveKey, areButtonsLinks }) => (
  <ButtonContainer>
    <div className="letter-buttons-container">
      {buttonList.map(({ key, buttonText, path }) =>
        areButtonsLinks ? (
          <StyledLink key={key} to={path} isActive={key === activeKey}>
            {buttonText || key}
          </StyledLink>
        ) : (
          <StyledButton
            key={key}
            isActive={key === activeKey}
            onClick={() => setActiveKey(key)}
          >
            {buttonText || key}
          </StyledButton>
        )
      )}
    </div>
  </ButtonContainer>
);

const FilterablePage = ({
  itemList,
  buttonList,
  defaultActiveKey = '',
  pageHeaderProps,
  cacheHit,
  isMobileDropdown = false,
  defaultMobileButtonText = '',
  areButtonsLinks = false,
  ...props
}) => {
  const [activeKey, setActiveKey] = useState(defaultActiveKey);
  const isEztrad = useShopId() === 'eztrad';
  const activeButton =
    activeKey && buttonList.find(({ key }) => key === activeKey);
  const mobileButtonText = activeButton
    ? activeButton.buttonText || activeButton.key
    : defaultMobileButtonText;

  return (
    <div {...props}>
      {!cacheHit ? (
        // Show a dummy header if we don't have cached data
        <PageHeaderLoadingState />
      ) : (
        <PageHeader {...pageHeaderProps} />
      )}
      <Above breakpoint="md">
        {match =>
          match ? (
            <Buttons
              buttonList={buttonList}
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              areButtonsLinks={areButtonsLinks}
            />
          ) : isMobileDropdown ? (
            <DropdownMenuContainer className={isEztrad ? eztradStyling : ''}>
              <DropdownMenu>
                <DropdownMenuButton>
                  {mobileButtonText}
                  <Carrot />
                </DropdownMenuButton>
                <DropdownMenuItems>
                  {buttonList.map(({ key, path, buttonText }) => (
                    <DropdownMenuItem
                      key={key}
                      onSelect={() => setActiveKey(key)}
                      closeOnSelect
                    >
                      {areButtonsLinks ? (
                        <StyledMobileLink
                          isActive={key === activeKey}
                          to={path}
                        >
                          {buttonText || key}
                        </StyledMobileLink>
                      ) : (
                        <DropdownMenuItemButton isActive={key === activeKey}>
                          {buttonText || key}
                        </DropdownMenuItemButton>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuItems>
              </DropdownMenu>
            </DropdownMenuContainer>
          ) : (
            <Buttons
              buttonList={buttonList}
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              areButtonsLinks={areButtonsLinks}
            />
          )
        }
      </Above>
      <MaxWidth>
        <div className="items-container">
          {itemList.map(({ key, Item }, index) => {
            if ((activeKey !== '' && key !== activeKey) || !Item) return null;
            return <Item key={index} />;
          })}
        </div>
      </MaxWidth>
    </div>
  );
};

export default FilterablePage;
